import { HIT_SLOP_PX, type View, timeToX } from "./view";

export interface Placed<T> {
  item: T;
  x0: number;
  x1: number;
}

export interface Spanned {
  start: string;
  end: string;
  /** Set on a block whose end is still moving. See `liveEndMs`. */
  on_air?: boolean;
}

/**
 * How far past its stored end a still-running block may be drawn.
 *
 * The worker extends the running row's span once per pass (worker.service
 * PASS_INTERVAL_SECONDS = 120), so the stored end trails the clock by up to two minutes. At the
 * one-minute zoom that is not a block a little too narrow, it is a block entirely off the left
 * of the screen. Drawing it to the clock is what makes the width grow with time.
 *
 * Capped, because on-air is derived with a five-minute grace (core.onair.GRACE): a show that
 * has actually stopped stays on air for five minutes while its stored end stands still.
 * Uncapped, that block would keep inflating for five minutes and then snap back by five
 * minutes when the flag finally cleared. One pass plus slack is as far as an extension can
 * honestly reach.
 */
export const LIVE_EXTEND_MAX_MS = 180_000;

/** Where a block's right edge reaches right now: its stored end, or the clock if it is live. */
export function liveEndMs(item: Spanned, nowMs: number | null): number {
  const stored = Date.parse(item.end);
  if (!item.on_air || nowMs === null) return stored;
  // Never shrink: a stored end past the clock is a fact about the data, not lag.
  return Math.max(stored, Math.min(nowMs, stored + LIVE_EXTEND_MAX_MS));
}

/**
 * How far behind the clock the recording may sit before the stretch in front of it is a hole in
 * the archive rather than the time map catching up.
 *
 * The newest recorded instant always trails the clock: the recorder extends the run it is writing
 * every few seconds (recorder.service RUN_FLUSH_SECONDS = 5) and the stream it reads is itself
 * behind the studio by several more. Drawn as a gap, that stretch puts a dead zone across the
 * block that is on air, on a station that is being recorded perfectly.
 *
 * Generous against the measured lag, and short enough that a recorder which has actually stopped
 * shows up as a hole within the minute. It can only ever hide the leading edge anyway: once the
 * recording resumes, that same hole is bounded at both ends and drawn like any other.
 */
export const LIVE_EDGE_FLUSH_MS = 60_000;

/**
 * Place blocks into pixel space, dropping anything off screen.
 *
 * Sub-pixel blocks are widened to a minimum so a two-second track does not vanish
 * entirely; without that, a jukebox hour at seven-day zoom would render as nothing at all
 * and the timeline would claim the station was silent.
 *
 * `nowMs` extends anything still running to the clock (see `liveEndMs`). Done here rather than
 * by rewriting the blocks before they arrive, for two reasons: culling then uses the extended
 * width, so a live block whose stored end is off the left of the screen is still drawn; and
 * this runs every frame, where a copy per block would allocate once per block per frame.
 */
export function place<T extends Spanned>(
  view: View,
  items: readonly T[],
  minWidth = 1,
  nowMs: number | null = null,
): Placed<T>[] {
  const out: Placed<T>[] = [];
  for (const item of items) {
    const x0 = timeToX(view, Date.parse(item.start));
    const x1 = timeToX(view, liveEndMs(item, nowMs));
    if (x1 < -HIT_SLOP_PX || x0 > view.width + HIT_SLOP_PX) continue;
    out.push({ item, x0, x1: Math.max(x1, x0 + minWidth) });
  }
  return out;
}

/**
 * Which placed block is under `x`, preferring the topmost drawn (last placed) one.
 *
 * Containment beats slop, whatever the draw order: a block the pointer is genuinely inside is
 * what was meant, and a neighbour reaching for the same pixel is not. Without that, a landing
 * a few pixels short of the next block's start selected the next block while the instant under
 * it -- which is what plays -- was still in the previous one.
 */
export function hit<T>(placed: readonly Placed<T>[], x: number, slop = 0): Placed<T> | null {
  let near: Placed<T> | null = null;
  for (let i = placed.length - 1; i >= 0; i--) {
    const candidate = placed[i]!;
    if (x >= candidate.x0 && x <= candidate.x1) return candidate;
    if (!near && x >= candidate.x0 - slop && x <= candidate.x1 + slop) near = candidate;
  }
  return near;
}

/**
 * What the playhead is inside, and so what the timeline should have selected.
 *
 * Tracks before shows, which is the pointer's own order: the jukebox row is the finer answer,
 * and the show is what is left where there is no row. A bucket standing for several collapsed
 * tracks is skipped -- it has no id to select -- so a zoomed-out lane falls through to the show
 * the way bare canvas does. Ends are exclusive and a running block reaches the clock, both as
 * everywhere else, so the block the playhead is drawn over is the block that is named.
 *
 * One exception, and it is why `selectedId` is an argument: a show keeps the border for as long
 * as the playhead is inside it. A show is picked deliberately -- from the timeline, the queue,
 * the list -- and the jukebox row under its first second taking the selection a frame later is
 * not a selection anybody made. Leaving the show ends it.
 */
export function selectionAtMs<T extends Spanned & { id: number | null }>(
  tracks: readonly T[],
  shows: readonly T[],
  ms: number,
  nowMs: number,
  selectedId: number | null,
): number | null {
  const covers = (item: T) => Date.parse(item.start) <= ms && liveEndMs(item, nowMs) > ms;
  if (selectedId !== null && shows.some((show) => show.id === selectedId && covers(show))) {
    return selectedId;
  }
  return (
    tracks.find((item) => item.id !== null && covers(item))?.id ??
    shows.find((item) => item.id !== null && covers(item))?.id ??
    null
  );
}
